import { useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import {
  ArrowRight, Bell, BookOpen, Building2, CalendarDays, ChevronRight, CircleHelp, Clock3, Cloud,
  ExternalLink, FileText, HeartHandshake, Home, Laptop, Layers, Library, Link as LinkIcon, Mail,
  MapPin, Menu, MoreHorizontal, Newspaper, Palette, Phone, Receipt, Search, Sparkles, UserRound,
  Users, X,
} from 'lucide-react'
import { catalog, findItem, isPreview, itemsOfType, search } from './lib/catalog'
import { formatDate, initials, typeLabels } from './lib/format'
import { Link, useRouter } from './lib/router'
import type { CatalogItem, ContentType, SearchDocument } from './types'

function MarkdownContent({ children }: { children: string }) {
  return <ReactMarkdown>{children}</ReactMarkdown>
}

function SourceStructure({ item }: { item: CatalogItem }) {
  const images = item.images.filter((image, index, all) => image.url && all.findIndex((candidate) => candidate.url === image.url) === index)
  if (!item.lists.length && !item.tables.length && !item.cards.length && !images.length) return null
  return <details className="source-structure"><summary>Captured source structure</summary><div>
    {item.lists.map((list, index) => list.ordered
      ? <ol key={`list-${index}`}>{list.items.map((entry, itemIndex) => <li key={itemIndex}>{entry}</li>)}</ol>
      : <ul key={`list-${index}`}>{list.items.map((entry, itemIndex) => <li key={itemIndex}>{entry}</li>)}</ul>)}
    {item.tables.map((table, index) => <div className="source-table-wrap" key={`table-${index}`}>
      {table.caption && <h2>{table.caption}</h2>}
      <table><tbody>{table.rows.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, cellIndex) => rowIndex === 0
        ? <th scope="col" key={cellIndex}>{cell}</th>
        : <td key={cellIndex}>{cell}</td>)}</tr>)}</tbody></table>
    </div>)}
    {item.cards.length > 0 && <div className="source-card-list">{item.cards.map((card, index) => <article key={index}>
      <h2>{card.url ? <a href={card.url} target="_blank" rel="noreferrer">{card.title}<ExternalLink size={13} aria-hidden="true" /></a> : card.title}</h2>
      {card.description && <p>{card.description}</p>}
    </article>)}</div>}
    {images.length > 0 && <div className="source-image-grid">{images.map((image) => <img src={image.url} alt={image.alt} key={image.url} loading="lazy" />)}</div>}
  </div></details>
}

const fallbackNav = [
  { to: '/', label: 'Home', icon: Home },
  { to: '/news', label: 'News', icon: Newspaper },
  { to: '/departments', label: 'Departments', icon: Building2 },
  { to: '/directory', label: 'Directory', icon: Users },
  { to: '/resources', label: 'Resources', icon: Library },
]

const resourceIcons: Record<string, typeof FileText> = {
  heart: HeartHandshake, laptop: Laptop, receipt: Receipt, palette: Palette, calendar: CalendarDays,
}

function displaySummary(item: CatalogItem | SearchDocument) {
  if (item.summary.trim()) return item.summary
  const navigationContext = item.tags.filter((tag) => tag !== item.title && tag !== item.department).slice(0, 2)
  return navigationContext.join(' · ') || 'Primary navigation overview'
}

function ItemLink({ item, children, className = '', onClick }: { item: CatalogItem | SearchDocument, children: ReactNode, className?: string, onClick?: () => void }) {
  const path = 'url' in item && item.url ? item.url : `/item/${item.slug}`
  const external = 'url' in item && Boolean(item.url)
  return external
    ? <a className={className} href={path} target="_blank" rel="noreferrer" onClick={onClick}>{children}</a>
    : <Link className={className} to={path} onClick={onClick}>{children}</Link>
}

function PreviewBanner() {
  if (!isPreview) return null
  return (
    <div className="preview-banner" role="status">
      <Sparkles size={15} aria-hidden="true" />
      <span><strong>Preview content</strong> — temporary samples disappear automatically when real items are added under <code>content/</code>.</span>
    </div>
  )
}

function SearchPanel({ open, onClose }: { open: boolean, onClose: () => void }) {
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const { navigate } = useRouter()
  const results = useMemo(() => search(query, 'all', 6), [query])

  useEffect(() => {
    if (open) {
      returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
      document.body.style.overflow = 'hidden'
      window.setTimeout(() => inputRef.current?.focus(), 10)
      return () => { document.body.style.overflow = ''; returnFocusRef.current?.focus() }
    }
    setQuery('')
  }, [open])

  useEffect(() => {
    if (!open) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = [...(dialogRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])') ?? [])]
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  if (!open) return null
  const submit = (event: FormEvent) => {
    event.preventDefault()
    navigate(`/search?q=${encodeURIComponent(query)}`)
    onClose()
  }
  return (
    <div className="search-backdrop" onMouseDown={onClose} role="presentation">
      <div id="site-search-dialog" ref={dialogRef} className="search-dialog" role="dialog" aria-modal="true" aria-label="Search the intranet" onMouseDown={(event) => event.stopPropagation()}>
        <form className="search-dialog-form" onSubmit={submit}>
          <Search size={22} aria-hidden="true" />
          <input ref={inputRef} type="search" autoComplete="off" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search news, people, resources…" aria-label="Search query" />
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close search"><X size={20} /></button>
        </form>
        <div className="search-dialog-body">
          <p className="search-hint" role="status" aria-live="polite" aria-atomic="true">{query ? `${results.length} quick result${results.length === 1 ? '' : 's'}` : 'Try a name, team, topic, or document'}</p>
          {query && results.length === 0 && <EmptyState title="No quick results" copy="Try a shorter term or browse the main sections." />}
          {query && results.map((result) => (
            <ItemLink item={result} className="quick-result" key={result.id} onClick={onClose}>
              <TypeGlyph type={result.type} />
              <span><strong>{result.title}</strong><small>{typeLabels[result.type]}{result.department ? ` · ${result.department}` : ''}</small></span>
              <ChevronRight size={17} aria-hidden="true" />
            </ItemLink>
          ))}
        </div>
        <div className="search-dialog-footer"><span><kbd>↵</kbd> see all results</span><span><kbd>esc</kbd> close</span></div>
      </div>
    </div>
  )
}

function Shell({ children }: { children: ReactNode }) {
  const [searchOpen, setSearchOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [expandedSection, setExpandedSection] = useState<string | null>(null)
  const primaryNavRef = useRef<HTMLElement>(null)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const { pathname } = useRouter()
  const activeItemSlug = pathname.startsWith('/item/') ? decodeURIComponent(pathname.slice('/item/'.length)) : null
  useEffect(() => { setMenuOpen(false); setExpandedSection(null); setSearchOpen(false); window.scrollTo(0, 0) }, [pathname])
  useEffect(() => {
    const shortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault(); setSearchOpen(true)
      }
    }
    window.addEventListener('keydown', shortcut)
    return () => window.removeEventListener('keydown', shortcut)
  }, [])
  useEffect(() => {
    if (!menuOpen) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !window.matchMedia('(max-width: 780px)').matches) return
      event.preventDefault()
      setExpandedSection(null)
      setMenuOpen(false)
      window.requestAnimationFrame(() => menuButtonRef.current?.focus())
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [menuOpen])
  useEffect(() => {
    if (!expandedSection) return
    const closeExpandedSection = (restoreFocus: boolean) => {
      const trigger = primaryNavRef.current?.querySelector<HTMLButtonElement>('.nav-trigger[aria-expanded="true"]')
      setExpandedSection(null)
      if (restoreFocus) window.requestAnimationFrame(() => trigger?.focus())
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (menuOpen && window.matchMedia('(max-width: 780px)').matches) return
      event.preventDefault()
      closeExpandedSection(true)
    }
    const handlePointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !primaryNavRef.current?.contains(event.target)) closeExpandedSection(false)
    }
    window.addEventListener('keydown', handleKeyDown)
    document.addEventListener('pointerdown', handlePointerDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [expandedSection, menuOpen])
  return (
    <div className="app-shell" data-content-revision={catalog.contentRevision} data-release="weekly-comm-2026-08-04" style={{ '--accent': catalog.site.accent } as CSSProperties}>
      <a className="skip-link" href="#main">Skip to content</a>
      <PreviewBanner />
      <aside className="app-rail" aria-label="Application links">
        <a href="https://fossswimschool-my.sharepoint.com/" target="_blank" rel="noreferrer" title="OneDrive" aria-label="OneDrive"><Cloud size={16} aria-hidden="true" /></a>
        <a href="https://www.wrike.com/workspace.htm" target="_blank" rel="noreferrer" title="Wrike" aria-label="Wrike"><span className="rail-wrike" aria-hidden="true">W</span></a>
        <a href="https://app.powerbi.com/" target="_blank" rel="noreferrer" title="Power BI" aria-label="Power BI"><span className="rail-power" aria-hidden="true">▥</span></a>
        <a href="https://sso.dayforcehcm.com/foss" target="_blank" rel="noreferrer" title="Dayforce" aria-label="Dayforce"><span className="rail-dayforce" aria-hidden="true">d</span></a>
        <a href="https://foss.bridgeapp.com/" target="_blank" rel="noreferrer" title="Fossiversity" aria-label="Fossiversity"><BookOpen size={16} aria-hidden="true" /></a>
        <a href="https://harbor.fossswimschool.com/" target="_blank" rel="noreferrer" title="Harbor" aria-label="Harbor"><Layers size={16} aria-hidden="true" /></a>
        <a href="https://www.office.com/" target="_blank" rel="noreferrer" title="Office 365" aria-label="Office 365"><span className="rail-office" aria-hidden="true">■</span></a>
        <button type="button" aria-label="More applications"><MoreHorizontal size={17} /></button>
      </aside>
      <header className="site-header">
        <div className="header-inner">
          <Link className="brand" to="/" aria-label={`${catalog.site.name} home`}>
            <img className="brand-logo" src="/media/bubble/bubble-logo.png" alt="The Bubble" />
          </Link>
          <nav id="primary-navigation" ref={primaryNavRef} className={`primary-nav ${menuOpen ? 'is-open' : ''}`} aria-label="Primary navigation">
            {catalog.navigation?.length ? catalog.navigation.map((section, index) => {
              const expanded = expandedSection === section.label
              const current = Boolean(activeItemSlug && (
                section.slug === activeItemSlug
                || section.groups.some((group) => group.items.some((item) => item.slug === activeItemSlug))
              ))
              const menuId = `primary-menu-${index}`
              return (
              <div
                className={`nav-section ${expanded ? 'is-expanded' : ''} ${current ? 'is-current' : ''}`}
                key={section.label}
                onMouseEnter={() => setExpandedSection(section.label)}
                onMouseLeave={() => setExpandedSection((current) => current === section.label ? null : current)}
              >
                <button
                  type="button"
                  className="nav-trigger"
                  aria-haspopup="true"
                  aria-expanded={expanded}
                  aria-controls={menuId}
                  aria-current={current ? 'page' : undefined}
                  data-current={current ? 'true' : undefined}
                  onClick={() => setExpandedSection(expanded ? null : section.label)}
                >{section.label}<ChevronRight size={14} /></button>
                <div className="mega-menu" id={menuId} aria-hidden={!expanded}>
                  {section.slug && <Link className="section-overview" to={`/item/${section.slug}`}>View {section.label} overview <ArrowRight size={14} /></Link>}
                  {section.groups.map((group) => <section key={group.label}><h2>{group.label}</h2>{group.items.map((item) => <Link key={item.slug} to={`/item/${item.slug}`}>{item.label}</Link>)}</section>)}
                </div>
              </div>
              )
            }) : fallbackNav.map(({ to, label, icon: Icon }) => <Link className={pathname === to ? 'active' : ''} key={to} to={to}><Icon size={17} />{label}</Link>)}
          </nav>
          <div className="header-actions">
            {catalog.site.supportUrl && <a className="support-link" href={catalog.site.supportUrl} target="_blank" rel="noreferrer"><CircleHelp size={17} />Help</a>}
            <button
              className="header-search"
              onClick={() => setSearchOpen(true)}
              aria-label="Search the intranet"
              aria-haspopup="dialog"
              aria-expanded={searchOpen}
              aria-controls="site-search-dialog"
            ><Search size={18} aria-hidden="true" /><span>Search</span><kbd aria-hidden="true">⌘ K</kbd></button>
            <button className="notification-button" type="button" aria-label="View notifications"><Bell size={18} /></button>
            <span className="profile-dot" aria-label="Profile"><UserRound size={19} /></span>
            <button
              ref={menuButtonRef}
              className="menu-button"
              onClick={() => { setMenuOpen((value) => !value); setExpandedSection(null) }}
              aria-controls="primary-navigation"
              aria-expanded={menuOpen}
              aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
            >{menuOpen ? <X /> : <Menu />}</button>
          </div>
        </div>
      </header>
      <main id="main">{children}</main>
      <footer className="site-footer">
        <div><img className="footer-logo" src="/media/bubble/bubble-logo.png" alt="The Bubble" /></div>
        <p>Home · News &amp; Events · Here at FOSS · People Resources · SSC Tools · Field Tools</p>
      </footer>
      <SearchPanel open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  )
}

function TypeGlyph({ type }: { type: ContentType }) {
  const Icon = { news: Newspaper, department: Building2, person: UserRound, resource: FileText, page: BookOpen, event: CalendarDays }[type]
  return <span className={`type-glyph type-${type}`}><Icon size={18} aria-hidden="true" /></span>
}

function SectionHeading({ eyebrow, title, to, linkLabel = 'View all' }: { eyebrow?: string, title: string, to?: string, linkLabel?: string }) {
  return <div className="section-heading"><div>{eyebrow && <p className="eyebrow">{eyebrow}</p>}<h2>{title}</h2></div>{to && <Link to={to}>{linkLabel}<ArrowRight size={16} /></Link>}</div>
}

function HomePage() {
  const [homeQuery, setHomeQuery] = useState('')
  const { navigate } = useRouter()
  const weeklyComms = itemsOfType('news')
    .filter((item) => item.tags.includes('The Weekly Comm'))
    .sort((left, right) => right.date.localeCompare(left.date))
    .slice(0, 10)
  const spotlight = [
    { title: 'All Field Call - June 2026', owner: 'Emily Heurung', age: '28 days ago', image: '/media/bubble/spotlight-field-call.webp', width: 1154, height: 769, summary: `Take a look at the summary of June's All Field Call.` },
    { title: 'Fourth of July Water Safety Reminders', owner: 'Emily Heurung', age: 'a month ago', image: '/media/bubble/spotlight-water-safety.webp', width: 1154, height: 649, summary: 'Water safety reminders to help our FOSS team enjoy a fun and safe holiday weekend.' },
    { title: 'Another Exciting Announcement', owner: `Craig O'Halloran`, age: 'a month ago', image: '/media/bubble/spotlight-top-workplace.webp', width: 1154, height: 649, summary: `We're thrilled to share that we were awarded spot #8 on the 2026 Top Workplaces List by St. Louis Post-Dispatch!` },
    { title: 'Improved Pool Grids and Teacher Schedules', owner: 'Emily Heurung', age: 'a month ago', image: '/media/bubble/spotlight-pool-grids.webp', width: 1154, height: 542, summary: 'Take a look at the enhancements that have been made to pool grids & teacher schedules.' },
  ]
  const events = [
    { day: '04', month: 'AUG', year: '26', title: 'Fall 2026 Enrollment Opens' },
    { day: '10', month: 'AUG', year: '26', title: 'Summer 2026 Program Evaluations' },
    { day: '17', month: 'AUG', year: '26', title: 'Swimsuit Sale: 20% off all suits!' },
    { day: '30', month: 'AUG', year: '26', title: 'Last Day of Summer 2026' },
    { day: '31', month: 'AUG', year: '26', title: 'First Day of Fall 2026' },
  ]
  const submitSearch = (event: FormEvent) => {
    event.preventDefault()
    navigate(`/search?q=${encodeURIComponent(homeQuery)}`)
  }
  return (
    <>
      <section className="bubble-search-band">
        <form className="bubble-home-search page-width" onSubmit={submitSearch}>
          <Search size={20} aria-hidden="true" />
          <input type="search" value={homeQuery} onChange={(event) => setHomeQuery(event.target.value)} placeholder="Who or what are you searching for?" aria-label="Search The Bubble" />
        </form>
      </section>

      <section className="bubble-story">
        <div className="page-width bubble-story-grid">
          <div className="bubble-story-copy">
            <p className="story-age">8 days ago · 7 · 4</p>
            <h1>IT'S SELF-CARE DAY! HOW ARE YOU TAKING CARE OF YOU?</h1>
            <p>Explore self-care tips, recharge yourself, and inspire others in the comments.</p>
            <Link className="bubble-read-more" to="/news">Read More <ArrowRight size={14} /></Link>
          </div>
          <img src="/media/bubble/hero-self-care.webp" alt="A leafy tree canopy viewed from below" width="1080" height="1080" fetchPriority="high" />
        </div>
      </section>

      <div className="page-width bubble-home-content">
        <section className="bubble-weekly-section">
          <div className="bubble-section-title">
            <div><p>News &amp; Events</p><h2>The Weekly Comm</h2></div>
            <Link to="/news">View all <ArrowRight size={15} /></Link>
          </div>
          <div className="weekly-comm-grid">
            {weeklyComms.map((item, index) => <ItemLink item={item} className={`weekly-comm-card ${index === 0 ? 'weekly-featured' : ''}`} key={item.id}>
              <div className="weekly-date"><strong>{new Date(`${item.date}T12:00:00`).getDate()}</strong><span>{new Intl.DateTimeFormat('en-US', { month: 'short' }).format(new Date(`${item.date}T12:00:00`))}</span></div>
              <div><p>{item.owner} · {formatDate(item.date)}</p><h3>{item.title}</h3><span>{item.summary}</span></div>
              <ChevronRight size={18} aria-hidden="true" />
            </ItemLink>)}
          </div>
        </section>

        <section className="bubble-spotlight-section">
          <h2>FOSS SPOTLIGHT</h2>
          <div className="bubble-zigzag" aria-hidden="true" />
          <div className="spotlight-grid">
            {spotlight.map((item) => {
              const matched = catalog.items.find((candidate) => candidate.title.toLowerCase() === item.title.toLowerCase())
              const content = <><img src={item.image} alt="" width={item.width} height={item.height} loading="lazy" /><p>{item.owner} · {item.age}</p><h3>{item.title}</h3><span>{item.summary}</span><small>♧ 0 · ◯ 0</small></>
              return matched ? <ItemLink item={matched} className="spotlight-card" key={item.title}>{content}</ItemLink> : <article className="spotlight-card" key={item.title}>{content}</article>
            })}
          </div>
        </section>
      </div>

      <section className="bubble-events">
        <div className="page-width">
          <h2>UPCOMING EVENTS (SSC)</h2>
          <div className="bubble-zigzag" aria-hidden="true" />
          <div className="bubble-events-grid">
            <div><h3>FOSS Calendar</h3><div className="source-events-list">{events.map((event) => <div className="source-event" key={event.title}><div><strong>{event.day}</strong><span>{event.month} {event.year}</span></div><p>{event.title}</p></div>)}</div></div>
            <div className="bubble-calendar"><h3>Latest Weekly Communications</h3><p className="calendar-range">June 1 – Aug. 3, 2026</p>{weeklyComms.slice(0, 5).map((item) => <ItemLink item={item} className="calendar-row" key={item.id}><span>{formatDate(item.date)}</span><strong>{item.title.replace(/^The Weekly Comm:\s*/i, '')}</strong></ItemLink>)}</div>
          </div>
        </div>
      </section>

      <section className="bubble-requests page-width">
        <h2>Submit a Request</h2>
        <div>{[
          { label: 'IT', icon: Laptop },
          { label: 'L&D', icon: BookOpen },
          { label: 'Creative', icon: Palette },
          { label: 'Project Intake', icon: FileText },
          { label: 'FP&A', icon: Receipt },
        ].map(({ label, icon: Icon }) => <Link to="/resources" key={label}><Icon size={18} />{label}</Link>)}</div>
      </section>
    </>
  )
}

function ResourceCard({ item }: { item: CatalogItem }) {
  const Icon = resourceIcons[item.icon] ?? FileText
  return <ItemLink item={item} className="resource-card"><span><Icon size={23} /></span><div><h3>{item.title}</h3><p>{displaySummary(item)}</p></div><ArrowRight className="resource-arrow" size={17} /></ItemLink>
}

function PageIntro({ eyebrow, title, copy }: { eyebrow: string, title: string, copy: string }) {
  return <section className="page-intro"><div className="page-width"><p className="eyebrow">{eyebrow}</p><h1>{title}</h1><p>{copy}</p></div></section>
}

function NewsPage() {
  const items = itemsOfType('news')
  return <><PageIntro eyebrow="What’s happening" title="News & updates" copy="Organization-wide updates, workplace news, and stories from across the team." /><div className="page-width page-body"><div className="news-grid">{items.map((item) => <article className="news-card" key={item.id}><div className="news-card-art">{item.image ? <img src={item.image} alt="" /> : <Newspaper size={37} />}</div><div><p className="meta">{item.tags.find((tag) => tag !== 'Sample') || 'News'}{item.date ? ` · ${formatDate(item.date)}` : ''}</p><h2><ItemLink item={item}>{item.title}</ItemLink></h2><p>{displaySummary(item)}</p><ItemLink item={item} className="text-link">Read more <ArrowRight size={15} /></ItemLink></div></article>)}</div>{!items.length && <EmptyState title="No published news" copy="Add news entries under content/ to populate this page." />}</div></>
}

function DepartmentsPage() {
  const items = itemsOfType('department')
  return <><PageIntro eyebrow="How we work" title="Departments" copy="Find the people, services, and resources that help every team move forward." /><div className="page-width page-body"><div className="department-grid">{items.map((item, index) => <ItemLink item={item} className={`department-card dept-tone-${index % 4}`} key={item.id}><span><Building2 size={25} /></span><p className="eyebrow">Department</p><h2>{item.title}</h2><p>{displaySummary(item)}</p><div>{item.location && <small><MapPin size={14} />{item.location}</small>}<strong>Visit department <ArrowRight size={16} /></strong></div></ItemLink>)}</div>{!items.length && <EmptyState title="No departments yet" copy="Department entries appear here automatically." />}</div></>
}

function DirectoryPage() {
  const [query, setQuery] = useState('')
  const people = itemsOfType('person').filter((person) => `${person.title} ${person.role} ${person.department} ${person.location}`.toLowerCase().includes(query.toLowerCase()))
  return <><PageIntro eyebrow="People & expertise" title="Employee directory" copy="Find colleagues by name, role, department, or location." /><div className="page-width page-body"><label className="directory-search"><Search size={20} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search the directory" /><span>{people.length} {people.length === 1 ? 'person' : 'people'}</span></label><div className="people-grid">{people.map((item, index) => <ItemLink item={item} className="person-card" key={item.id}><div className={`avatar avatar-${index % 5}`}>{item.image ? <img src={item.image} alt="" /> : initials(item.title)}</div><div><h2>{item.title}</h2><p>{item.role || 'Role not listed'}</p><small>{item.department}{item.location ? ` · ${item.location}` : ''}</small></div><ChevronRight size={18} /></ItemLink>)}</div>{!people.length && <EmptyState title={query ? 'No people found' : 'No directory entries yet'} copy={query ? 'Try another name, team, or location.' : 'Person entries appear here automatically.'} />}</div></>
}

function ResourcesPage() {
  const items = itemsOfType('resource')
  const groups = [...new Set(items.map((item) => item.department || 'General'))]
  return <><PageIntro eyebrow="Tools & guidance" title="Resources" copy="The forms, policies, systems, and guidance people use most." /><div className="page-width page-body resource-page">{groups.map((group) => <section key={group}><SectionHeading title={group} /><div className="resource-list">{items.filter((item) => (item.department || 'General') === group).map((item) => <ResourceCard item={item} key={item.id} />)}</div></section>)}{!items.length && <EmptyState title="No resources yet" copy="Resource entries appear here automatically." />}</div></>
}

function SearchPage() {
  const { pathname, search: searchString, navigate } = useRouter()
  const params = useMemo(() => new URLSearchParams(searchString), [searchString])
  const query = params.get('q') ?? ''
  const type = (params.get('type') ?? 'all') as ContentType | 'all'
  const results = useMemo(() => search(query, type), [query, type])
  const replaceParams = (next: URLSearchParams) => navigate(`${pathname}${next.size ? `?${next}` : ''}`, true)
  const updateQuery = (value: string) => { const next = new URLSearchParams(params); if (value) next.set('q', value); else next.delete('q'); replaceParams(next) }
  const updateType = (value: string) => { const next = new URLSearchParams(params); if (value === 'all') next.delete('type'); else next.set('type', value); replaceParams(next) }
  return <><PageIntro eyebrow="Find anything" title="Search" copy="Instant results from the local catalog—no server round trip required." /><div className="page-width search-page"><label className="search-page-input"><Search size={22} aria-hidden="true" /><input autoFocus type="search" autoComplete="off" aria-label="Search the intranet" value={query} onChange={(event) => updateQuery(event.target.value)} placeholder="Search news, people, departments, and resources" /></label><div className="search-toolbar"><p role="status" aria-live="polite" aria-atomic="true"><strong>{results.length}</strong> result{results.length === 1 ? '' : 's'}{query ? <> for “{query}”</> : ''}</p><select value={type} onChange={(event) => updateType(event.target.value)} aria-label="Filter by content type"><option value="all">All content</option>{Object.entries(typeLabels).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></div><div className="search-results">{results.map((result) => <ItemLink item={result} className="search-result" key={result.id}><TypeGlyph type={result.type} /><div><p className="meta">{typeLabels[result.type]}{result.department ? ` · ${result.department}` : ''}</p><h2>{result.title}</h2><p>{result.role || displaySummary(result)}</p>{result.tags.length > 0 && <div className="tag-row">{result.tags.slice(0, 3).map((tag) => <span key={tag}>{tag}</span>)}</div>}</div><ChevronRight size={19} /></ItemLink>)}</div>{!results.length && <EmptyState title="No results found" copy="Try a shorter term, check the spelling, or choose another content type." />}</div></>
}

function navigationContextFor(item: CatalogItem) {
  for (const section of catalog.navigation) {
    for (const group of section.groups) {
      if (group.items.some((entry) => entry.slug === item.slug)) {
        return {
          section: section.label,
          group: group.label,
          siblings: group.items.filter((entry) => entry.slug !== item.slug),
        }
      }
    }
  }
  return null
}

function DetailPage() {
  const { pathname } = useRouter()
  const slug = decodeURIComponent(pathname.slice('/item/'.length))
  const item = findItem(slug)
  if (!item) return <NotFound />
  if (item.type === 'person') return <PersonDetail item={item} />
  const navigationContext = navigationContextFor(item)
  const sourceState = item.sourceStatus === 'source_empty'
    ? <EmptyState title="No source content" copy="This route is intentionally empty in the legacy source." />
    : item.sourceStatus === 'privacy_unavailable'
      ? <EmptyState title="Unavailable in this public-safe migration" copy="Employee directory and reporting details are excluded to protect privacy." />
      : <EmptyState title="Content unavailable" copy="No publishable source body is available for this route." />
  return <div className="detail-page"><div className="detail-hero"><div className="page-width detail-heading">{navigationContext ? <nav className="breadcrumbs" aria-label="Breadcrumb"><Link to="/">Home</Link><ChevronRight size={13} aria-hidden="true" /><span>{navigationContext.section}</span><ChevronRight size={13} aria-hidden="true" /><span>{navigationContext.group}</span><ChevronRight size={13} aria-hidden="true" /><span aria-current="page">{item.title}</span></nav> : <Link className="back-link" to={routeForType(item.type)}>← Back to {pluralLabel(item.type)}</Link>}<p className="eyebrow">{item.eyebrow || typeLabels[item.type]}</p><h1>{item.title}</h1><p>{displaySummary(item)}</p><div className="detail-meta">{item.owner && <span><UserRound size={16} />{item.owner}</span>}{item.updatedAt && <span><CalendarDays size={16} />{item.updatedAt}</span>}{item.readTime && <span><Clock3 size={16} />{item.readTime}</span>}{item.date && <span><CalendarDays size={16} />{formatDate(item.date, item.type === 'event')}</span>}{item.department && <span><Building2 size={16} />{item.department}</span>}{item.location && <span><MapPin size={16} />{item.location}</span>}</div></div></div><div className="page-width detail-layout"><article className="markdown-body">{item.body ? <><MarkdownContent>{item.body}</MarkdownContent><SourceStructure item={item} /></> : sourceState}</article><aside className="detail-sidebar"><h2>Details</h2>{item.owner && <div><small>Content owner</small><strong>{item.owner}</strong></div>}{item.audience.length > 0 && <div><small>Audience</small><strong>{item.audience.join(', ')}</strong></div>}{item.tags.length > 0 && <div><small>Topics</small><div className="tag-row">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div></div>}{item.links.length > 0 && <div className="source-links"><small>Links in this message</small>{item.links.map((link, index) => <a href={link.url} target="_blank" rel="noreferrer" key={`${link.url}-${index}`}>{link.text}<ExternalLink size={12} aria-hidden="true" /></a>)}</div>}{navigationContext?.siblings.length ? <div className="related-links"><small>Also in {navigationContext.group}</small>{navigationContext.siblings.slice(0, 5).map((entry) => <Link key={entry.slug} to={`/item/${entry.slug}`}>{entry.label}<ChevronRight size={13} aria-hidden="true" /></Link>)}</div> : null}{item.url && <a className="button button-dark" href={item.url} target="_blank" rel="noreferrer">Open resource <ExternalLink size={16} /></a>}</aside></div></div>
}

function PersonDetail({ item }: { item: CatalogItem }) {
  return <div className="person-detail"><div className="page-width"><Link className="back-link" to="/directory">← Back to directory</Link><div className="profile-card"><div className="avatar avatar-large">{item.image ? <img src={item.image} alt="" /> : initials(item.title)}</div><div><p className="eyebrow">Employee directory</p><h1>{item.title}</h1><p className="profile-role">{item.role}</p><div className="profile-facts">{item.department && <span><Building2 size={17} />{item.department}</span>}{item.location && <span><MapPin size={17} />{item.location}</span>}</div></div><div className="profile-actions">{item.email && <a className="button button-dark" href={`mailto:${item.email}`}><Mail size={16} />Email</a>}{item.phone && <a className="button button-secondary" href={`tel:${item.phone}`}><Phone size={16} />Call</a>}</div></div>{item.body && <article className="markdown-body profile-bio"><MarkdownContent>{item.body}</MarkdownContent></article>}</div></div>
}

function routeForType(type: ContentType) {
  return type === 'news' ? '/news' : type === 'department' ? '/departments' : type === 'person' ? '/directory' : type === 'resource' ? '/resources' : '/'
}
function pluralLabel(type: ContentType) { return type === 'person' ? 'directory' : type === 'news' ? 'news' : `${type}s` }

function EmptyState({ title, copy }: { title: string, copy: string }) {
  return <div className="empty-state"><LinkIcon size={24} /><h3>{title}</h3><p>{copy}</p></div>
}

function NotFound() {
  return <div className="page-width not-found"><span>404</span><h1>That page isn’t here.</h1><p>The link may be outdated, or the content may not be in the current catalog.</p><Link className="button button-dark" to="/">Return home</Link></div>
}

export default function App() {
  const { pathname } = useRouter()
  const routeTitle = pathname === '/' ? ''
    : pathname === '/news' ? 'News'
      : pathname === '/departments' ? 'Departments'
        : pathname === '/directory' ? 'Directory'
          : pathname === '/resources' ? 'Resources'
            : pathname === '/search' ? 'Search'
              : pathname.startsWith('/item/') ? findItem(decodeURIComponent(pathname.slice('/item/'.length)))?.title ?? 'Page not found'
                : 'Page not found'
  useEffect(() => {
    document.title = routeTitle ? `${routeTitle} | The Bubble` : 'The Bubble'
  }, [routeTitle])
  const page = pathname === '/' ? <HomePage />
    : pathname === '/news' ? <NewsPage />
      : pathname === '/departments' ? <DepartmentsPage />
        : pathname === '/directory' ? <DirectoryPage />
          : pathname === '/resources' ? <ResourcesPage />
            : pathname === '/search' ? <SearchPage />
              : pathname.startsWith('/item/') ? <DetailPage />
                : <NotFound />
  return <Shell>{page}</Shell>
}
